import { signIn, confirmSignIn } from '../util/amplifyAuth';
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { manageActivation } from "../components/API";
import { completeSignInAndRedirect } from "../util/authNav";
import Spinner from "../components/Spinner/Spinner";
import PasswordInput from "../components/PasswordInput";
import PasswordRequirements, { usePasswordValidation } from "../components/PasswordRequirements";
import useTestimonials from "../hooks/useTestimonials";
import "../styles/PasswordInput.css";
import "../styles/LoginPage.css";

function Login() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [formError, setFormError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // New password challenge flow
    const [needsNewPassword, setNeedsNewPassword] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // Forgot password
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotSent, setForgotSent] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotError, setForgotError] = useState("");

    const openForgot = useCallback(() => {
        setForgotEmail(username);
        setForgotSent(false);
        setForgotError("");
        setShowForgot(true);
        window.history.pushState({ forgot: true }, '');
    }, [username]);

    const closeForgot = useCallback(() => {
        setShowForgot(false);
    }, []);

    // Listen for browser back to close forgot password view
    useEffect(() => {
        const onPopState = (e) => {
            if (showForgot) {
                setShowForgot(false);
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [showForgot]);

    const handleBackFromForgot = () => {
        window.history.back();
    };

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const textReviews = useTestimonials();
    const redirectParam = searchParams.get('redirect');

    const completeLogin = () => completeSignInAndRedirect(navigate, redirectParam);

    const handleSignIn = async (e) => {
        e.preventDefault();
        setFormError("");
        setSubmitting(true);

        try {
            const result = await signIn({ username, password });

            if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                setNeedsNewPassword(true);
                setSubmitting(false);
                return;
            }

            await completeLogin();
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const { valid: passwordValid } = usePasswordValidation(newPassword);

    const handleNewPassword = async (e) => {
        e.preventDefault();
        setFormError("");

        if (!passwordValid) {
            setFormError("Please meet all password requirements.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setFormError("Passwords do not match.");
            return;
        }

        setSubmitting(true);

        try {
            await confirmSignIn({ challengeResponse: newPassword });
            await completeLogin();
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!forgotEmail) return;
        setForgotLoading(true);
        setForgotError("");
        try {
            await manageActivation('request-reset', { email: forgotEmail });
            setForgotSent(true);
        } catch {
            setForgotError("There was a problem. Please contact support@primemaxing.com");
        }
        setForgotLoading(false);
    };

    // New password challenge screen
    if (needsNewPassword) {
        return (
            <div className="login-page">
                <div className="login-overlay">
                    <div className="login-modal">
                        <h1>Set New Password</h1>
                        <p className="login-subtitle">
                            Please set a new password for your account.
                        </p>

                        {formError && <div className="login-error">{formError}</div>}

                        <form className="login-form" onSubmit={handleNewPassword}>
                            <div className="form-group">
                                <label htmlFor="login-new-password">New Password</label>
                                <PasswordInput
                                    id="login-new-password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                                <PasswordRequirements password={newPassword} />
                            </div>

                            <div className="form-group">
                                <label htmlFor="login-confirm-password">Confirm Password</label>
                                <PasswordInput
                                    id="login-confirm-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                            </div>

                            <button
                                className="login-submit"
                                type="submit"
                                disabled={submitting}
                            >
                                {submitting ? <Spinner size={20} color="#fff" /> : 'Set Password'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // Forgot password screen (replaces login form)
    if (showForgot) {
        return (
            <div className="login-page">
                <div className="login-overlay">
                    <div className="login-modal">
                        <button className="login-back-btn" onClick={handleBackFromForgot}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
                            </svg>
                            Back to login
                        </button>
                        <h1>Need Help?</h1>
                        <p className="login-subtitle">
                            Enter your email and we'll send you a link to reset your password or activate your account.
                        </p>

                        <div className="login-form">
                            <div className="form-group">
                                <label htmlFor="forgot-email">Email</label>
                                <input
                                    id="forgot-email"
                                    type="email"
                                    value={forgotEmail}
                                    onChange={(e) => { setForgotEmail(e.target.value); setForgotSent(false); setForgotError(""); }}
                                    autoFocus
                                />
                            </div>

                            {forgotError && (
                                <div className="login-error">{forgotError}</div>
                            )}
                            {forgotSent && (
                                <p className="login-forgot-confirmation">
                                    If an account exists, a link has been sent to your email.
                                </p>
                            )}

                            <button
                                className="login-submit"
                                type="button"
                                onClick={handleForgotPassword}
                                disabled={forgotLoading || !forgotEmail}
                            >
                                {forgotLoading ? <Spinner size={20} color="#fff" /> : 'Send Link'}
                            </button>
                        </div>

                        <hr className="login-divider" />

                        <p className="login-signup-link">
                            Don't have an account?{" "}
                            <a href="/signup" onClick={(e) => { e.preventDefault(); navigate(redirectParam ? `/signup?redirect=${encodeURIComponent(redirectParam)}` : "/signup"); }}>Create one</a>
                        </p>

                        <div className="login-testimonials-ticker">
                            <div className="ticker-track">
                                {[...textReviews, ...textReviews].map((review, i) => (
                                    <div className="ticker-card" key={i}>
                                        <p>"{review.quote}"</p>
                                        <span>— {review.author}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Main login screen
    return (
        <div className="login-page">
            <div className="login-overlay">
                <div className="login-modal">
                    <h1>Welcome Back</h1>
                    <p className="login-subtitle">
                        Log in to access your personalized health insights.
                    </p>

                    {formError && <div className="login-error">{formError}</div>}

                    <form className="login-form" onSubmit={handleSignIn}>
                        <div className="form-group">
                            <label htmlFor="login-email">Email</label>
                            <input
                                id="login-email"
                                type="email"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="login-password">Password</label>
                            <PasswordInput
                                id="login-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        <p className="login-forgot-link">
                            <a href="#" onClick={(e) => { e.preventDefault(); openForgot(); }}>
                                Forgot your password?
                            </a>
                        </p>

                        <button
                            className="login-submit"
                            type="submit"
                            disabled={submitting}
                        >
                            {submitting ? <Spinner size={20} color="#fff" /> : 'Log In'}
                        </button>
                    </form>

                    <hr className="login-divider" />

                    <p className="login-signup-link">
                        Don't have an account?{" "}
                        <a href="/signup" onClick={(e) => { e.preventDefault(); navigate(redirectParam ? `/signup?redirect=${encodeURIComponent(redirectParam)}` : "/signup"); }}>Create one</a>
                    </p>

                    <div className="login-testimonials-ticker">
                        <div className="ticker-track">
                            {[...textReviews, ...textReviews].map((review, i) => (
                                <div className="ticker-card" key={i}>
                                    <p>"{review.quote}"</p>
                                    <span>— {review.author}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
